/**
 * FCFPanel - FCF predictions with live data
 */

import { useEffect, useState } from 'react'
import type { CompanyData } from '../../types'
import { getPredictions, formatCurrency, type PredictionsData } from '../../services/api'
import { PrivateCompanyBanner, PrivateCompanyNoDataMessage } from '../shared/PrivateCompanyBanner'

interface FCFPanelProps {
  company: CompanyData | null
  initialData?: PredictionsData | null
}

interface DataFreshness {
  source: 'live' | 'cached' | 'loading' | 'error'
  timestamp?: string
  age?: string
}

const formatAge = (timestamp: string): string => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

const isDataFresh = (timestamp: string): boolean => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  return diffMs < 60000 // Within 1 minute
}

export function FCFPanel({ company, initialData }: FCFPanelProps) {
  const [predictions, setPredictions] = useState<PredictionsData | null>(initialData || null)
  const [freshness, setFreshness] = useState<DataFreshness>({
    source: initialData ? 'cached' : 'loading',
    timestamp: initialData ? new Date().toISOString() : undefined,
  })

  useEffect(() => {
    // Track if the effect is still active (for cleanup)
    let isActive = true

    if (!company?.ticker) {
      setPredictions(null)
      setFreshness({ source: 'loading' })
      return
    }

    // Skip fetching for private companies - they don't have public FCF data
    if (company.isPrivate) {
      setPredictions(null)
      // Don't set to 'loading' for private companies - they won't load public data
      setFreshness({ source: 'cached' })
      return
    }

    // Reset state when company changes
    setPredictions(null)
    setFreshness({ source: 'loading' })

    // Show cached/initial data immediately if available
    if (initialData) {
      setPredictions(initialData)
      setFreshness({
        source: 'cached',
        timestamp: new Date().toISOString(),
        age: 'loading fresh data...',
      })
    }

    // Fetch live data
    const fetchData = async () => {
      try {
        const result = await getPredictions(company.ticker)

        // Check if effect is still active before updating state
        if (!isActive) return

        if (result.data) {
          // Validate that the response has expected predictions data properties
          // The API might return { error: '...', ticker: '...' } for missing data
          const data = result.data as PredictionsData & { timestamp?: string; dataSource?: string; error?: string }

          if (data.error || !data.predictions || !Array.isArray(data.predictions)) {
            // API returned an error or missing data structure
            console.warn('Predictions API returned incomplete data:', data)
            setFreshness({ source: 'error' })
            return
          }

          setPredictions(result.data)
          const timestamp = data.timestamp || new Date().toISOString()
          const isFresh = isDataFresh(timestamp)
          setFreshness({
            source: isFresh ? 'live' : 'cached',
            timestamp,
            age: formatAge(timestamp),
          })
        } else if (result.error) {
          console.error('Predictions API error:', result.error)
          setFreshness({ source: 'error' })
        } else {
          // Neither data nor error - unexpected response
          console.warn('Predictions API returned unexpected response:', result)
          setFreshness({ source: 'error' })
        }
      } catch (error) {
        console.error('Failed to fetch predictions:', error)
        if (isActive) {
          setFreshness({ source: 'error' })
        }
      }
    }

    fetchData()

    // Cleanup function to prevent stale state updates
    return () => {
      isActive = false
    }
  }, [company?.ticker, company?.isPrivate, initialData])

  if (!company) {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>Select a company to view FCF predictions</p>
      </div>
    )
  }

  // Handle private companies - show banner and no-data message
  const isPrivate = company.isPrivate || company.quoteType === 'PRIVATE'
  if (isPrivate) {
    return (
      <div className="space-y-6">
        <PrivateCompanyBanner companyName={company.name} />

        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-term-text">
            💰 FCF Predictions - {company.ticker}
            <span className="ml-2 px-2 py-0.5 bg-term-info/30 text-term-info text-xs rounded font-bold">PRIVATE</span>
          </h2>
        </div>

        <PrivateCompanyNoDataMessage
          dataType="FCF Predictions"
          suggestion="Request historical cash flow statements to build custom projections."
        />

        {/* Manual Analysis Suggestions */}
        <div className="term-box p-4">
          <h3 className="text-sm font-medium text-term-text mb-3">📋 Data Needed for FCF Analysis</h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              'Cash Flow Statements (3-5 years)',
              'Operating Cash Flow Breakdown',
              'CapEx Schedule & Projections',
              'Working Capital Requirements',
              'Depreciation & Amortization Schedule',
              'Non-Recurring Items Detail',
              'Management FCF Projections',
              'Industry Comparable Analysis',
            ].map((doc, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <span className="text-term-warning">○</span>
                <span className="text-term-textDim">{doc}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-term-textDim mt-4">
            💡 Once you have historical cash flows, you can build a custom projection model.
          </p>
        </div>
      </div>
    )
  }

  if (!predictions && freshness.source === 'loading') {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>Loading FCF predictions for {company.ticker}...</p>
      </div>
    )
  }

  if (!predictions && freshness.source === 'error') {
    return (
      <div className="flex items-center justify-center h-full text-term-error">
        <p>Failed to load FCF predictions for {company.ticker}</p>
      </div>
    )
  }

  if (!predictions) {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>No FCF predictions available for {company.ticker}</p>
      </div>
    )
  }

  // Calculate totals
  const totalProjectedFcf = predictions.predictions.reduce((acc, p) => acc + p.fcf, 0)
  const avgConfidence = predictions.predictions.reduce((acc, p) => acc + p.confidence, 0) / predictions.predictions.length

  // Check if this is an ETF or fund
  console.log('FCFPanel predictions:', predictions)
  console.log('FCFPanel quoteType:', predictions.quoteType)
  const isETF = predictions.quoteType === 'ETF'
  const isMutualFund = predictions.quoteType === 'MUTUALFUND'
  const isNonEquity = isETF || isMutualFund
  const isInsufficientData = predictions.model === 'insufficient_data'
  console.log('FCFPanel isETF:', isETF, 'isNonEquity:', isNonEquity)

  return (
    <div className="space-y-6">
      {/* ETF/Fund Warning Banner */}
      {isNonEquity && (
        <div className="bg-term-warning/20 border border-term-warning rounded-lg p-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{isETF ? '📦' : '🏦'}</span>
            <div>
              <h3 className="text-term-warning font-bold text-lg">
                {isETF ? 'ETF - No FCF Data Available' : 'Mutual Fund - No FCF Data Available'}
              </h3>
              <p className="text-term-textDim text-sm">
                {isETF
                  ? 'ETFs are investment funds that hold multiple securities. They don\'t generate free cash flow themselves - consider analyzing the individual holdings instead.'
                  : 'Mutual funds don\'t have traditional cash flow metrics. Consider analyzing the underlying holdings for FCF analysis.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Insufficient Data Warning (for non-ETFs) */}
      {isInsufficientData && !isNonEquity && (
        <div className="bg-term-info/20 border border-term-info rounded-lg p-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">ℹ️</span>
            <div>
              <h3 className="text-term-info font-bold text-lg">Insufficient Data for Predictions</h3>
              <p className="text-term-textDim text-sm">
                There isn't enough historical financial data available to generate reliable FCF predictions for this company.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header with freshness indicator */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-term-text">
          💰 FCF Predictions - {company.ticker}
          {isETF && <span className="ml-2 px-2 py-0.5 bg-term-warning/30 text-term-warning text-xs rounded font-bold">ETF</span>}
          {isMutualFund && <span className="ml-2 px-2 py-0.5 bg-term-info/30 text-term-info text-xs rounded font-bold">FUND</span>}
        </h2>
        <div className="flex items-center gap-4">
          <span className="term-badge term-badge-info">
            Model: {predictions.model || 'PE Prediction Engine'}
          </span>
          <FreshnessIndicator freshness={freshness} />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="term-box p-4">
          <div className="text-term-textDim text-xs mb-1">Current FCF</div>
          <div className="text-2xl font-bold text-term-info">
            {formatCurrency(predictions.currentFcf)}
          </div>
        </div>
        <div className="term-box p-4">
          <div className="text-term-textDim text-xs mb-1">5Y Projected Total</div>
          <div className="text-2xl font-bold text-term-success">
            {formatCurrency(totalProjectedFcf)}
          </div>
        </div>
        <div className="term-box p-4">
          <div className="text-term-textDim text-xs mb-1">Model Confidence</div>
          <div className="text-2xl font-bold text-term-info">
            {((predictions.modelConfidence || avgConfidence) * 100).toFixed(0)}%
          </div>
          <ConfidenceBar value={predictions.modelConfidence || avgConfidence} />
        </div>
        <div className="term-box p-4">
          <div className="text-term-textDim text-xs mb-1">Avg Growth Rate</div>
          <div className={`text-2xl font-bold ${predictions.avgGrowthRate && predictions.avgGrowthRate > 0 ? 'text-term-success' : 'text-term-warning'}`}>
            {predictions.avgGrowthRate !== undefined ? `${(predictions.avgGrowthRate * 100).toFixed(1)}%` : 'N/A'}
          </div>
        </div>
      </div>

      {/* Historical FCF (if available) */}
      {predictions.historicalFCF && predictions.historicalFCF.length > 0 && (
        <div className="term-box p-4">
          <h3 className="text-sm font-medium text-term-text mb-3">Historical FCF</h3>
          <div className="flex items-end gap-2 h-24">
            {predictions.historicalFCF.map((h, idx) => {
              const maxFcf = Math.max(...predictions.historicalFCF!.map(x => x.fcf))
              const height = (h.fcf / maxFcf) * 100
              return (
                <div key={idx} className="flex flex-col items-center flex-1">
                  <div
                    className="w-full bg-term-info/50 rounded-t"
                    style={{ height: `${height}%` }}
                    title={formatCurrency(h.fcf)}
                  />
                  <span className="text-xs text-term-textDim mt-1">{h.year}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Predictions Table */}
      <div className="overflow-x-auto">
        <table className="term-table table-fixed">
          <thead>
            <tr>
              <th className="w-[15%] text-left">Year</th>
              <th className="w-[30%] text-right">Predicted FCF</th>
              <th className="w-[25%] text-right">Growth Rate</th>
              <th className="w-[30%] text-center">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {predictions.predictions.map((p) => (
              <tr key={p.year}>
                <td className="w-[15%] font-medium">{p.year}</td>
                <td className="w-[30%] text-right text-term-success font-medium">
                  {formatCurrency(p.fcf)}
                </td>
                <td className={`w-[25%] text-right ${(p.growth ?? 0) >= 0 ? 'text-term-success' : 'text-term-error'}`}>
                  {p.growth !== undefined && p.growth !== null
                    ? `${p.growth >= 0 ? '+' : ''}${(p.growth * 100).toFixed(1)}%`
                    : 'N/A'}
                </td>
                <td className="w-[30%] text-center">
                  <div className="inline-flex items-center gap-2 justify-center">
                    <ConfidenceBar value={p.confidence} mini />
                    <span className="text-xs text-term-textDim w-10">
                      {(p.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Key Drivers */}
      {predictions.keyDrivers && predictions.keyDrivers.length > 0 && (
        <div className="term-box p-4">
          <h3 className="text-sm font-medium text-term-text mb-3">Key FCF Drivers</h3>
          <div className="flex flex-wrap gap-2">
            {predictions.keyDrivers.map((driver, idx) => (
              <span key={idx} className="term-badge term-badge-info">
                {driver}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Model Info */}
      <div className="term-box p-4">
        <h3 className="text-sm font-medium text-term-text mb-2">Model Details</h3>
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-term-textDim">Sector:</span>{' '}
            <span className="text-term-text">{company.sector}</span>
          </div>
          <div>
            <span className="text-term-textDim">Industry:</span>{' '}
            <span className="text-term-text">{company.industry}</span>
          </div>
          <div>
            <span className="text-term-textDim">Model:</span>{' '}
            <span className="text-term-text">{predictions.model || 'growth_extrapolation'}</span>
          </div>
          <div>
            <span className="text-term-textDim">Data Source:</span>{' '}
            <span className={freshness.source === 'live' ? 'text-term-success' : 'text-term-warning'}>
              {freshness.source === 'live' ? 'Live (Yahoo Finance + SEC)' : 'Cached'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

interface FreshnessIndicatorProps {
  freshness: DataFreshness
}

function FreshnessIndicator({ freshness }: FreshnessIndicatorProps) {
  const config = {
    live: { label: 'LIVE', color: 'text-term-success', bg: 'bg-term-success/20', icon: '●' },
    cached: { label: 'CACHED', color: 'text-term-warning', bg: 'bg-term-warning/20', icon: '○' },
    loading: { label: 'LOADING', color: 'text-term-info', bg: 'bg-term-info/20', icon: '◌' },
    error: { label: 'ERROR', color: 'text-term-error', bg: 'bg-term-error/20', icon: '✕' },
  }

  const { label, color, bg, icon } = config[freshness.source]

  return (
    <div className={`flex items-center gap-2 px-2 py-1 rounded ${bg}`}>
      <span className={color}>{icon}</span>
      <span className={`text-xs font-medium ${color}`}>{label}</span>
      {freshness.age && (
        <span className="text-xs text-term-textDim">({freshness.age})</span>
      )}
    </div>
  )
}

interface ConfidenceBarProps {
  value: number
  mini?: boolean
}

function ConfidenceBar({ value, mini = false }: ConfidenceBarProps) {
  const getColor = () => {
    if (value >= 0.75) return 'bg-term-success'
    if (value >= 0.5) return 'bg-term-info'
    if (value >= 0.25) return 'bg-term-warning'
    return 'bg-term-error'
  }

  return (
    <div className={`term-progress ${mini ? 'h-1.5 w-16' : 'h-2 mt-2'}`}>
      <div
        className={`term-progress-bar ${getColor()}`}
        style={{ width: `${value * 100}%` }}
      />
    </div>
  )
}
